import React, { useState } from 'react';
import { Webhook, Plus, Trash2, ToggleLeft, ToggleRight, Copy, CheckCircle, XCircle } from 'lucide-react';

const WebhookManager = ({ webhooks, onAdd, onDelete, onToggle }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ name: '', url: '', events: [] });
  const [copied, setCopied] = useState(null);

  const eventOptions = ['lead.created', 'lead.updated', 'call.completed', 'email.sent', 'sms.sent', 'deal.won', 'deal.lost'];

  const handleCopy = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleAdd = () => {
    if (newWebhook?.name && newWebhook?.url) {
      onAdd?.(newWebhook);
      setNewWebhook({ name: '', url: '', events: [] });
      setShowAddForm(false);
    }
  };

  const toggleEvent = (event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev?.events?.includes(event)
        ? prev?.events?.filter(e => e !== event)
        : [...prev?.events, event]
    }));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Webhook className="w-5 h-5 text-orange-600" />
          <h2 className="text-base font-semibold text-gray-900">Webhook Management</h2>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Webhook
        </button>
      </div>
      {showAddForm && (
        <div className="px-6 py-4 bg-orange-50 border-b border-orange-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Webhook name"
              value={newWebhook?.name}
              onChange={e => setNewWebhook(prev => ({ ...prev, name: e?.target?.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="url"
              placeholder="https://your-endpoint.com/webhook"
              value={newWebhook?.url}
              onChange={e => setNewWebhook(prev => ({ ...prev, url: e?.target?.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">Select events to listen for:</p>
            <div className="flex flex-wrap gap-2">
              {eventOptions?.map(event => (
                <button
                  key={event}
                  onClick={() => toggleEvent(event)}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    newWebhook?.events?.includes(event)
                      ? 'bg-orange-600 text-white' :'bg-white border border-gray-300 text-gray-600 hover:border-orange-400'
                  }`}
                >
                  {event}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors">Save</button>
            <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
          </div>
        </div>
      )}
      <div className="divide-y divide-gray-50">
        {webhooks?.map((wh) => (
          <div key={wh?.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900">{wh?.name}</span>
                  {wh?.active
                    ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    : <XCircle className="w-3.5 h-3.5 text-gray-400" />}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-mono truncate max-w-xs">{wh?.url}</span>
                  <button onClick={() => handleCopy(wh?.url, wh?.id)} className="text-gray-400 hover:text-blue-600 transition-colors">
                    {copied === wh?.id ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {wh?.events?.map(ev => (
                    <span key={ev} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{ev}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <div className="text-right hidden md:block">
                  <p className="text-xs text-gray-400">Deliveries</p>
                  <p className="text-sm font-medium text-gray-700">{wh?.deliveries}</p>
                </div>
                <div className="text-right hidden md:block">
                  <p className="text-xs text-gray-400">Failed</p>
                  <p className={`text-sm font-medium ${wh?.failed > 0 ? 'text-red-600' : 'text-green-600'}`}>{wh?.failed}</p>
                </div>
                <button onClick={() => onToggle?.(wh?.id)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors">
                  {wh?.active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                </button>
                <button onClick={() => onDelete?.(wh?.id)} className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WebhookManager;
